import { useEffect, useMemo, useRef } from "react";
import { Col, Row } from "react-bootstrap";
import MaterialIcon from "../../oversight-core/ui-elements/material-icon/material-icon";
import Helper from "../../oversight-core/utils/helpers";
import styles from "./live-energy-flow-card.module.scss";

export type EnergyFlowDirection = "in" | "out";

export interface IEnergyFlow {
  powerKw?: number;
  active?: boolean;
  direction?: EnergyFlowDirection;
}

interface IProps {
  onClick?: () => void;
  showHeader?: boolean;
  grid?: IEnergyFlow;
  solar?: IEnergyFlow;
  ev?: IEnergyFlow;
  load?: IEnergyFlow;
}

type NodeKey = "grid" | "solar" | "ev" | "load";

const PARTICLE_COUNT = 3;
const DURATION_MS = 1800;

const PATH_D: Record<NodeKey, string> = {
  grid: "M 168 150 L 168 73 Q 168 65 160 65 L 110 65",
  solar: "M 192 150 L 192 73 Q 192 65 200 65 L 250 65",
  ev: "M 168 210 L 168 287 Q 168 295 160 295 L 110 295",
  load: "M 192 210 L 192 287 Q 192 295 200 295 L 250 295",
};

const NODE_COLOR: Record<NodeKey, string> = {
  grid: "#E8784A",
  solar: "#F5A623",
  ev: "#8BC34A",
  load: "#31B9CC",
};

const HEX_COLOR_PATTERN = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const normalizeHexColor = (hex: string): string => {
  const match = HEX_COLOR_PATTERN.exec(hex.trim());
  if (!match) {
    throw new Error(`Invalid hex color: "${hex}"`);
  }

  let normalized = match[1];
  if (normalized.length === 3) {
    normalized = normalized
      .split("")
      .map((char) => char + char)
      .join("");
  }

  return normalized;
};

const lightenColor = (hex: string, amount = 0.45): string => {
  const normalized = normalizeHexColor(hex);
  const num = Number.parseInt(normalized, 16);
  const r = Math.min(255, ((num >> 16) & 0xff) + Math.round(255 * amount));
  const g = Math.min(255, ((num >> 8) & 0xff) + Math.round(255 * amount));
  const b = Math.min(255, (num & 0xff) + Math.round(255 * amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const PARTICLE_COLOR: Record<NodeKey, string> = {
  grid: lightenColor(NODE_COLOR.grid),
  solar: lightenColor(NODE_COLOR.solar),
  ev: lightenColor(NODE_COLOR.ev),
  load: lightenColor(NODE_COLOR.load),
};

const DEFAULT_DIRECTION: Record<NodeKey, EnergyFlowDirection> = {
  grid: "in",
  solar: "in",
  ev: "out",
  load: "out",
};

const NODE_KEYS: NodeKey[] = ["grid", "solar", "ev", "load"];

const resolveFlow = (
  key: NodeKey,
  flow?: IEnergyFlow,
): { powerKw: number; active: boolean; direction: EnergyFlowDirection } => {
  const powerKw = Number(flow?.powerKw ?? 0);
  return {
    powerKw,
    active: flow?.active ?? powerKw > 0,
    direction: flow?.direction ?? DEFAULT_DIRECTION[key],
  };
};

const LiveEnergyFlowCard = (props: IProps) => {
  const { onClick, showHeader = true } = props;

  const flows = useMemo(
    () => ({
      grid: resolveFlow("grid", props.grid),
      solar: resolveFlow("solar", props.solar),
      ev: resolveFlow("ev", props.ev),
      load: resolveFlow("load", props.load),
    }),
    [props.grid, props.solar, props.ev, props.load],
  );

  const pathRefs = useRef<Record<NodeKey, SVGPathElement | null>>({
    grid: null,
    solar: null,
    ev: null,
    load: null,
  });
  const particleRefs = useRef<Record<NodeKey, (SVGCircleElement | null)[]>>({
    grid: [],
    solar: [],
    ev: [],
    load: [],
  });

  const flowSignature = NODE_KEYS.map(
    (key) => `${key}:${flows[key].active}:${flows[key].direction}`,
  ).join("|");
  const hasActiveFlow = NODE_KEYS.some((key) => flows[key].active);

  useEffect(() => {
    NODE_KEYS.forEach((key) => {
      if (!flows[key].active) {
        particleRefs.current[key]?.forEach((circle) =>
          circle?.setAttribute("opacity", "0"),
        );
      }
    });

    if (!hasActiveFlow) {
      return;
    }

    let frame = 0;
    const activeKeys = NODE_KEYS.filter((key) => flows[key].active);

    const tick = (now: number) => {
      activeKeys.forEach((key) => {
        const path = pathRefs.current[key];
        const particles = particleRefs.current[key];
        const flow = flows[key];
        if (!path || !particles?.length) return;

        const length = path.getTotalLength();
        particles.forEach((circle, index) => {
          if (!circle) return;
          const progress = (now / DURATION_MS + index / PARTICLE_COUNT) % 1;
          const distance =
            (flow.direction === "out" ? progress : 1 - progress) * length;
          const point = path.getPointAtLength(distance);
          circle.setAttribute("cx", String(point.x));
          circle.setAttribute("cy", String(point.y));
          circle.setAttribute("opacity", "1");
        });
      });
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [flowSignature, flows, hasActiveFlow]);

  return (
    <div
      className={`${
        showHeader ? "bg-white p-3 rounded-4" : ""
      } h-100 d-flex flex-column`}
    >
      {showHeader && (
        <Row className="align-items-center">
          <Col className="text-link font-size-16 font-weight-400">
            Live Energy Flow
          </Col>
          {onClick && (
            <Col xs="auto">
              <MaterialIcon
                icon="chevron_forward"
                size={24}
                className="cursor-pointer"
                color="var(--dark)"
                onClick={onClick}
              />
            </Col>
          )}
        </Row>
      )}
      <div className={styles.flowContainer}>
        <svg
          className={styles.paths}
          viewBox="0 0 360 360"
          fill="none"
          aria-hidden="true"
        >
          {NODE_KEYS.map((key) => (
            <g key={key}>
              <path
                ref={(el) => {
                  pathRefs.current[key] = el;
                }}
                d={PATH_D[key]}
                className={
                  flows[key].active ? styles.pathActive : styles.pathIdle
                }
                stroke={NODE_COLOR[key]}
              />
              {Array.from({ length: PARTICLE_COUNT }).map((_, index) => (
                <circle
                  key={`${key}-${index}`}
                  ref={(el) => {
                    if (!particleRefs.current[key]) {
                      particleRefs.current[key] = [];
                    }
                    particleRefs.current[key][index] = el;
                  }}
                  r="2"
                  fill={PARTICLE_COLOR[key]}
                  opacity="0"
                  className={styles.particle}
                  style={{ color: PARTICLE_COLOR[key] }}
                />
              ))}
            </g>
          ))}
        </svg>

        <div className={`${styles.outerNode} ${styles.topLeft}`}>
          <MaterialIcon icon="cell_tower" size={28} color={NODE_COLOR.grid} />
          <span className={styles.powerValue}>
            {Helper.roundTo2(flows.grid.powerKw)} kW
          </span>
        </div>

        <div className={`${styles.outerNode} ${styles.topRight}`}>
          <MaterialIcon icon="solar_power" size={28} color={NODE_COLOR.solar} />
          <span className={styles.powerValue}>
            {Helper.roundTo2(flows.solar.powerKw)} kW
          </span>
        </div>

        <div className={styles.centerNode}>
          <MaterialIcon icon="lightbulb" size={28} color="#7483CC" />
        </div>

        <div className={`${styles.outerNode} ${styles.bottomLeft}`}>
          <MaterialIcon icon="ev_station" size={28} color={NODE_COLOR.ev} />
          <span className={styles.powerValue}>
            {Helper.roundTo2(flows.ev.powerKw)} kW
          </span>
        </div>

        <div className={`${styles.outerNode} ${styles.bottomRight}`}>
          <MaterialIcon icon="roofing" size={28} color={NODE_COLOR.load} />
          <span className={styles.powerValue}>
            {Helper.roundTo2(flows.load.powerKw)} kW
          </span>
        </div>
      </div>
    </div>
  );
};

export default LiveEnergyFlowCard;
